import {
    carryClock,
    clamp01,
    strokeClock,
} from '@/features/landing/stroke/stroke-clock'

// Carry (landing spec §3.1): the previous kanji shrinks into its slot and
// turns into a guideline; the new part's guideline arrives from its side;
// the kanji is then written in true stroke order with the carried block
// darkening at its turn. Timings are the prototype's.

export interface Box {
    x: number
    y: number
    w: number
    h: number
}

export function unionBox(boxes: Box[]): Box {
    const x = Math.min(...boxes.map((b) => b.x))
    const y = Math.min(...boxes.map((b) => b.y))
    const r = Math.max(...boxes.map((b) => b.x + b.w))
    const btm = Math.max(...boxes.map((b) => b.y + b.h))
    return { x, y, w: r - x, h: btm - y }
}

export function lerpBox(a: Box, b: Box, t: number): Box {
    const l = (p: number, q: number) => p + (q - p) * t
    return { x: l(a.x, b.x), y: l(a.y, b.y), w: l(a.w, b.w), h: l(a.h, b.h) }
}

export function fitTransform(
    from: Box,
    to: Box
): { transform: string; scale: number } {
    const sx = to.w / from.w
    const sy = to.h / from.h
    const tx = to.x - from.x * sx
    const ty = to.y - from.y * sy
    const r = (v: number) => Number(v.toFixed(3))
    return {
        transform: `translate(${r(tx)} ${r(ty)}) scale(${r(sx)} ${r(sy)})`,
        scale: Math.sqrt(sx * sy),
    }
}

export function contains(outer: Box, inner: Box): boolean {
    return (
        inner.x >= outer.x &&
        inner.y >= outer.y &&
        inner.x + inner.w <= outer.x + outer.w &&
        inner.y + inner.h <= outer.y + outer.h
    )
}

export function dirFrom(from: Box, to: Box): { dx: number; dy: number } {
    const dx = to.x + to.w / 2 - (from.x + from.w / 2)
    const dy = to.y + to.h / 2 - (from.y + from.h / 2)
    const len = Math.hypot(dx, dy) || 1
    return { dx: dx / len, dy: dy / len }
}

export function easeInOut(x: number): number {
    return x < 0.5 ? 2 * x * x : 1 - Math.pow(-2 * x + 2, 2) / 2
}

export function frameAt(progress: number, steps = 4): { i: number; t: number } {
    const p = clamp01(progress) * steps
    const i = Math.min(steps - 1, Math.floor(p))
    return { i, t: i === steps - 1 && progress >= 1 ? 1 : clamp01(p - i) }
}

export function plainLayers(t: number, n: number): number[] {
    return strokeClock(clamp01(t / 0.6) * n, n)
}

export interface CarryStep {
    move: number
    grey: number
    hand: number
    arrive: number
    writeT: number
    labelFlipped: boolean
    drawn: number[]
    opacity: number[]
    carriedInk: number
    ghostOpacity: number[]
    cGuideOpacity: number
}

// A 0..1 ramp from `start` over `dur`, rounded so the boundaries land
// exactly (0.35 − 0.27 over 0.08 is 0.9999999999999994 in floats).
function ramp(t: number, start: number, dur: number): number {
    return clamp01(Math.round(((t - start) / dur) * 1e9) / 1e9)
}

export function carryLayers(
    t: number,
    n: number,
    carried: ReadonlySet<number>
): CarryStep {
    const grey = ramp(t, 0, 0.3)
    const move = easeInOut(grey)
    const hand = ramp(t, 0.27, 0.08)
    const arrive = ramp(t, 0, 0.22)
    const writeT = ramp(t, 0.36, 0.52)
    const newCount = n - carried.size
    const q = writeT * (newCount + 1.5)
    const { drawn: raw, carried: carriedInk } = carryClock(q, n, carried)
    // Carried strokes never draw: once their turn starts they are whole
    // paths faded in by `opacity` (guide → ink as one block).
    const drawn = raw.map((d, j) =>
        carried.has(j) ? (carriedInk > 0 ? 1 : 0) : d
    )
    const opacity = raw.map((d, j) =>
        carried.has(j) ? carriedInk * hand : d > 0 ? 1 : 0
    )
    const ghostOpacity = raw.map((d, j) =>
        carried.has(j) ? 0 : arrive * (1 - clamp01((d - 0.85) / 0.15))
    )
    return {
        move,
        grey,
        hand,
        arrive,
        writeT,
        labelFlipped: t >= 0.3,
        drawn,
        opacity,
        carriedInk,
        ghostOpacity,
        cGuideOpacity: hand * (1 - carriedInk),
    }
}

const ARRIVALS = [0.02, 0.1, 0.16, 0.36, 0.88] as const

export function formulaReveal(
    t: number
): [boolean, boolean, boolean, boolean, boolean] {
    return ARRIVALS.map((a) => t >= a) as [
        boolean,
        boolean,
        boolean,
        boolean,
        boolean,
    ]
}
